import { Injectable } from '@angular/core';
import { Decimal } from 'decimal.js';

@Injectable({
  providedIn: 'root',
})
export class DecimalService {
  constructor() {
    // 例如，設定預設精度為 50 位
    Decimal.set({ precision: 50 });
  }

  /**
   * 檢查一個值是否為有效的數字字串或數字
   *
   * @param value 要檢查的值
   * @returns boolean
   */
  public isValid(value: string | number | undefined | null): boolean {
    if (value === null || value === undefined || value === '') {
      return false;
    }
    try {
      new Decimal(value);
      return true;
    } catch (error) {
      return false;
    }
  }

  /**
   * 高精度加法
   *
   * @param a 加數
   * @param b 被加數
   * @returns 計算結果的字串形式
   */
  public plus(a: string | number, b: string | number): string {
    const numA = new Decimal(a);
    const numB = new Decimal(b);
    return numA.plus(numB).toString();
  }

  /**
   * 高精度減法
   *
   * @param a 減數
   * @param b 被減數
   * @returns 計算結果的字串形式
   */
  public minus(a: string | number, b: string | number): string {
    const numA = new Decimal(a);
    const numB = new Decimal(b);
    return numA.minus(numB).toString();
  }

  /**
   * 高精度乘法
   *
   * @param a 乘數
   * @param b 被乘數
   * @returns 計算結果的字串形式
   */
  public multiply(a: string | number, b: string | number): string {
    const numA = new Decimal(a);
    const numB = new Decimal(b);
    return numA.times(numB).toString();
  }

  /**
   * 高精度除法
   *
   * @param a 除數
   * @param b 被除數
   * @returns 計算結果的字串形式
   */
  public divide(a: string | number, b: string | number): string {
    const numA = new Decimal(a);
    const numB = new Decimal(b);
    return numA.dividedBy(numB).toString();
  }

  /**
   * 比較 a 是否大於 b
   *
   * @param a
   * @param b
   * @returns boolean
   */
  public isGreaterThan(a: string | number, b: string | number): boolean {
    return new Decimal(a).greaterThan(new Decimal(b));
  }

  /**
   * 比較 a 是否小於 b
   *
   * @param a
   * @param b
   * @returns boolean
   */
  public isLessThan(a: string | number, b: string | number): boolean {
    return new Decimal(a).lessThan(new Decimal(b));
  }

  /**
   * 比較 a 是否等於 b
   *
   * @param a
   * @param b
   * @returns boolean
   */
  public isEqualTo(a: string | number, b: string | number): boolean {
    return new Decimal(a).equals(new Decimal(b));
  }

  /**
   * 格式化數字為(非指數)字串
   *
   * @param value 要格式化的值
   * @param decimalPlaces 小數位數
   * @returns 格式化後的字串
   */
  public toFixed(value: string | number | undefined | null, decimalPlaces?: number): string {
    if (!this.isValid(value)) return '';

    return new Decimal(value as number | string).toFixed(decimalPlaces);
  }
}
